/**
 * OMNI FLOW DIRECTOR — presentation for `flow-director-1`.
 *
 * What is executable now, in the charter's band vocabulary, plus what has
 * been recorded as changing. The contract's own first rule governs this
 * module too: **it maps, it does not classify.** Every band is a lookup
 * from the classification `what-can-start-2` already produced. Re-deriving
 * a band here would be a second readiness system, and the two copies would
 * disagree the first time a gate rule changed.
 *
 * Five rules carry the weight, and each has a test:
 *
 *  1. **BOTH NAMES ON EVERY FRONT.** A front carries `band` AND `status`
 *     from one classification. This module renders the pair and, when the
 *     payload's own mapping table disagrees with a front's pair, says so —
 *     drift between the two names is exactly what carrying both is for.
 *  2. **AN ABSENT CLEARANCE IS NOT A BLOCKED ONE.** IN_PROGRESS and
 *     COMPLETE have no "may it start" question, so their clearance is a
 *     stated NOT_APPLICABLE. A consumer that defaults an absent key reads
 *     it as blocked; this one refuses to.
 *  3. **ORDER CARRIES NO MEANING.** Fronts arrive ordered by instance id —
 *     a total order with zero interpretive content. This module never
 *     re-sorts, never scores, and exposes no key anything could be ranked
 *     by. The gating engine's readiness score is not in the payload and is
 *     not reconstructed here.
 *  4. **THE STORED STATE IS NOT THE LIVE ONE.** `readiness_state` is a
 *     persisted column from the last recalculation; the band is evaluated
 *     at read time. They may legitimately differ, and where they do the
 *     difference is shown rather than reconciled.
 *  5. **AN EMPTY TRANSITION LANE IS NOT A CALM PROJECT.** Recorded
 *     transitions are layered behind a separate capability. Where the
 *     reader does not hold it, the lane says so; it never renders as
 *     "nothing has happened".
 */
import { runtimeStateDisplay } from './runtimeVocabulary';

export const FLOW_CONTRACT = 'flow-director-1';

/**
 * The charter's band names. This table supplies WORDS, never the mapping:
 * the payload carries `bandVocabulary.mappedFrom`, and that is the
 * authority on which status became which band.
 */
const BAND_DISPLAY = {
  EXECUTABLE_NOW: {
    key: 'EXECUTABLE_NOW',
    label: 'Executable now',
    tone: 'good',
    meaning: 'Every condition this work depends on is satisfied.',
  },
  CONDITIONAL: {
    key: 'CONDITIONAL',
    label: 'Conditional',
    tone: 'attention',
    meaning: 'Nothing blocks outright; conditions remain outstanding.',
  },
  BLOCKED: {
    key: 'BLOCKED',
    label: 'Blocked',
    tone: 'bad',
    meaning: 'At least one condition prevents this work from proceeding.',
  },
  NOT_ESTABLISHED: {
    key: 'NOT_ESTABLISHED',
    label: 'Not established',
    tone: 'unknown',
    meaning:
      'OMNΛS cannot say whether this work can proceed. An open question — and not permission to start.',
  },
  IN_PROGRESS: {
    key: 'IN_PROGRESS',
    // Never `good`: running work on a "what can I start" list is how a crew
    // gets sent to a workface that already has one.
    label: 'In progress',
    tone: 'active',
    meaning: 'Crews are on this work. Whether it could start is no longer the open question.',
  },
  COMPLETE: {
    key: 'COMPLETE',
    label: 'Complete',
    tone: 'done',
    meaning: 'Recorded as finished. Whether it can start no longer applies.',
  },
};

export const bandDisplay = (band) =>
  BAND_DISPLAY[band] || {
    key: String(band ?? 'UNKNOWN'),
    label: 'A band this view does not name',
    tone: 'unknown',
    meaning:
      'The server named a band this view does not carry. It is shown verbatim rather than mapped onto one that looks close.',
  };

/* -------------------------------------------------------------------------
 * EXECUTION CLEARANCE — four words, and a stated non-answer
 * ---------------------------------------------------------------------- */

const CLEARANCE_DISPLAY = {
  CLEARED: {
    key: 'CLEARED',
    label: 'Cleared to start',
    tone: 'good',
  },
  CONDITIONAL: {
    key: 'CONDITIONAL',
    label: 'Cleared once conditions are met',
    tone: 'attention',
  },
  BLOCKED: {
    key: 'BLOCKED',
    label: 'Not cleared',
    tone: 'bad',
  },
  NOT_ESTABLISHED: {
    key: 'NOT_ESTABLISHED',
    label: 'Clearance not established',
    tone: 'unknown',
  },
};

export const CLEARANCE_NOT_APPLICABLE_LABEL = 'The start question does not arise';

/**
 * Clearance, including the case that matters most: **not applicable**.
 *
 * Work that has started or finished has no "may it start" answer. The
 * contract states that absence with its cause rather than omitting the key,
 * because an omitted key defaults to blocked in the reader's head. This
 * returns `applicable: false` for that case, never a BLOCKED tone.
 */
export const presentClearance = (clearance) => {
  if (!clearance || typeof clearance !== 'object') {
    return {
      applicable: false,
      value: null,
      absence: 'UNKNOWN',
      label: 'Clearance not stated',
      tone: 'unknown',
      basis: 'The server did not state a clearance for this front. Nothing has been assumed in its place.',
    };
  }
  if (clearance.value && CLEARANCE_DISPLAY[clearance.value]) {
    return {
      applicable: true,
      value: clearance.value,
      absence: null,
      ...CLEARANCE_DISPLAY[clearance.value],
      basis: clearance.basis || null,
    };
  }
  if (clearance.value) {
    return {
      applicable: true,
      value: clearance.value,
      absence: null,
      key: clearance.value,
      label: clearance.value,
      tone: 'unknown',
      basis: clearance.basis || null,
    };
  }
  return {
    applicable: false,
    value: null,
    absence: clearance.absence || 'NOT_APPLICABLE',
    key: clearance.absence || 'NOT_APPLICABLE',
    label: CLEARANCE_NOT_APPLICABLE_LABEL,
    // Deliberately its own tone. `bad` here would paint finished work red.
    tone: 'neutral',
    basis: clearance.basis || null,
  };
};

/* -------------------------------------------------------------------------
 * WORK FRONTS — both names, and the drift check that carrying both is for
 * ---------------------------------------------------------------------- */

export const BAND_DRIFT_NOTE =
  'This front’s band and status do not agree with the mapping this same payload published. Both are shown as received; nothing has been corrected, because a correction here would hide a disagreement between two names that are supposed to be one fact.';

export const STORED_STATE_DIFFERS_NOTE =
  'The stored readiness state and the band evaluated at read time differ. The band is the current answer; the stored column is what the last recalculation wrote. Neither is wrong — the inputs moved.';

/**
 * One work front.
 *
 * `bandAgrees` is the check the contract's "both names on every row" design
 * exists to make possible: the payload publishes `mappedFrom` AND stamps a
 * pair on every front, so a reader can verify the two names are one fact
 * rather than trusting that they are.
 */
export const presentWorkFront = (front, mappedFrom = {}) => {
  if (!front) return null;
  const band = bandDisplay(front.band);
  const status = front.status || null;
  const expectedBand = status ? mappedFrom[status] : undefined;
  const bandAgrees = expectedBand === undefined || expectedBand === front.band;
  const stored = front.storedReadinessState || null;
  const storedValue = stored ? stored.value ?? null : null;
  return {
    instanceId: front.instanceId,
    instanceName: front.instanceName || null,
    activityId: front.activityId || null,
    band,
    // The source classification's own word, carried beside the band rather
    // than replaced by it.
    status,
    statusDisplay: status ? runtimeStateDisplay(status) : null,
    bandAgrees,
    bandDriftNote: bandAgrees ? null : BAND_DRIFT_NOTE,
    expectedBand: bandAgrees ? null : expectedBand ?? null,
    clearance: presentClearance(front.executionClearance),
    canStart: front.canStart ?? null,
    governingConstraint: front.governingConstraint || null,
    blockingReasons: front.blockingReasons || [],
    conditions: front.conditions || [],
    missingFacts: front.missingFacts || [],
    evidence: front.evidence || [],
    unlockedByConstraints: front.unlockedByConstraints || [],
    earliestRealisticStart: front.earliestRealisticStart || null,
    storedReadinessState: storedValue,
    storedReadinessBasis: stored ? stored.basis || null : null,
    // Only a real disagreement is called one: an absent stored value is a
    // gap, not a conflict.
    storedDiffers: Boolean(storedValue) && storedValue !== front.band,
    storedDiffersNote:
      Boolean(storedValue) && storedValue !== front.band ? STORED_STATE_DIFFERS_NOTE : null,
  };
};

/* -------------------------------------------------------------------------
 * RECORDED TRANSITIONS — layered, and never silently empty
 * ---------------------------------------------------------------------- */

export const TRANSITIONS_NOT_AUTHORIZED_LABEL =
  'Recorded transitions are served under a separate capability this read does not carry.';

export const TRANSITIONS_EMPTY_LABEL =
  'No recorded transition matched this scan. That is what the audit record holds — not a statement that nothing moved.';

const TRANSITION_ACTION_LABELS = {
  state_changed: 'Readiness state changed',
  gates_reevaluated: 'Gates re-evaluated',
  readiness_recalculated: 'Readiness recalculated',
  completion_observed: 'Completion observed',
};

export const transitionActionLabel = (action) =>
  TRANSITION_ACTION_LABELS[action] || String(action ?? 'Recorded event');

export const presentTransition = (entry) => {
  if (!entry) return null;
  return {
    auditId: entry.auditId,
    action: entry.action || null,
    actionLabel: transitionActionLabel(entry.action),
    occurredAt: entry.occurredAt || null,
    instanceId: entry.instanceId || null,
    actorId: entry.actorId || null,
    trigger: entry.trigger ?? null,
    oldState: entry.oldState ?? null,
    newState: entry.newState ?? null,
    state: entry.state ?? null,
    blockingGates: entry.blockingGates ?? null,
    blockingReason: entry.blockingReason ?? null,
    oldBlockingGates: entry.oldBlockingGates ?? null,
    oldBlockingReason: entry.oldBlockingReason ?? null,
    gateChanges: entry.gateChanges ?? null,
    canStart: entry.canStart ?? null,
    actualStart: entry.actualStart ?? null,
    startedAtRisk: entry.startedAtRisk ?? null,
    // Both of these say "the record held more than this payload shows", and
    // both must reach the reader: one is a contract gap, the other a
    // deliberate withholding with its reason.
    unmappedFields: entry.unmappedFields || [],
    withheldFields: entry.withheldFields || null,
  };
};

/**
 * The transition lane in its three shapes.
 *
 * The refusal shape is the one that matters: a reader without the
 * capability must see that the lane EXISTS and why it is empty. Rendering
 * nothing would say "this project has had no recorded movement", which is a
 * different and false claim.
 */
export const presentRecordedTransitions = (lane) => {
  if (!lane || typeof lane !== 'object') {
    return {
      authorized: false,
      absence: 'UNKNOWN',
      headline: 'The recorded-transition lane was not carried in this response.',
      basis: null,
      entries: [],
      count: 0,
      truncated: false,
      actions: [],
      replayability: null,
      blockedWithoutRecordedHistory: null,
    };
  }
  if (lane.value === null || lane.value === undefined) {
    return {
      authorized: false,
      absence: lane.absence || 'UNKNOWN',
      headline:
        lane.absence === 'NOT_AUTHORIZED'
          ? TRANSITIONS_NOT_AUTHORIZED_LABEL
          : 'The recorded-transition lane could not answer.',
      basis: lane.basis || null,
      entries: [],
      count: 0,
      truncated: false,
      actions: lane.actions || [],
      replayability: lane.replayability || null,
      blockedWithoutRecordedHistory: null,
    };
  }
  const entries = (lane.value || []).map(presentTransition).filter(Boolean);
  return {
    authorized: true,
    absence: null,
    headline: entries.length ? null : TRANSITIONS_EMPTY_LABEL,
    basis: lane.basis || null,
    entries,
    // The server's own count, not the listed length: the listing is capped.
    count: lane.count ?? entries.length,
    truncated: lane.truncated === true,
    actions: lane.actions || [],
    replayability: lane.replayability || null,
    blockedWithoutRecordedHistory: presentBlockedWithoutHistory(
      lane.blockedWithoutRecordedHistory
    ),
  };
};

/**
 * "Instances are created BLOCKED and only TRANSITIONS emit events."
 *
 * Two shapes, and they must not collapse: an abstention off a cut scan is
 * not the same as a counted set. Reporting the abstention as a count of
 * zero would claim every blocked front has recorded history.
 */
export const presentBlockedWithoutHistory = (lane) => {
  if (!lane || typeof lane !== 'object') return null;
  if (lane.absence === 'SCAN_TRUNCATED' || lane.value === null) {
    return {
      determined: false,
      absence: lane.absence || 'SCAN_TRUNCATED',
      count: null,
      instanceIds: [],
      note: lane.note || null,
    };
  }
  return {
    determined: true,
    absence: null,
    count: lane.count ?? (lane.instanceIds || []).length,
    instanceIds: lane.instanceIds || [],
    note: lane.note || null,
  };
};

/* -------------------------------------------------------------------------
 * RESOURCE COMPATIBILITY — rule-less, not writer-less
 * ---------------------------------------------------------------------- */

export const presentResourceCompatibility = (family) => {
  if (!family) return null;
  return {
    value: family.value || 'UNKNOWN',
    absence: family.absence || 'UNKNOWN',
    structural: family.structural === true,
    causality: family.causality || 'NOT_ESTABLISHED',
    basis: family.basis || null,
    // Naming the landed writers is the whole point: without them this reads
    // as "no data yet", when the truth is that the columns are written and
    // no RULE compares them.
    writersPresent: family.writersPresent || [],
  };
};

/* -------------------------------------------------------------------------
 * UNKNOWNS — four kinds, two of which nest a constraint
 * ---------------------------------------------------------------------- */

const UNKNOWN_KINDS = {
  UNLINKED_HARD_GATE: {
    key: 'UNLINKED_HARD_GATE',
    label: 'A hard-gate requirement that gates no work front',
  },
  NO_INSTANCES: {
    key: 'NO_INSTANCES',
    label: 'No work fronts exist on this project',
  },
  LEVERAGE_UNKNOWN: {
    key: 'LEVERAGE_UNKNOWN',
    label: 'A constraint whose own state is unknown',
  },
  CONSTRAINT_HELD_ONLY_BY_UNKNOWN: {
    key: 'CONSTRAINT_HELD_ONLY_BY_UNKNOWN',
    label: 'A constraint standing only on an unknown',
  },
};

export const flowUnknownDisplay = (kind) =>
  UNKNOWN_KINDS[kind] || {
    key: String(kind ?? 'UNKNOWN'),
    label: 'A gap this view does not name',
  };

/**
 * The nested constraint is kept nested.
 *
 * A leverage entry carries its OWN `kind` — the constraint family — and
 * spreading it up would overwrite the discriminator a reader filters on.
 * The backend refuses that spread; so does this.
 */
export const presentFlowUnknown = (unknown) => {
  if (!unknown) return null;
  return {
    kind: unknown.kind || null,
    display: flowUnknownDisplay(unknown.kind),
    detail: unknown.detail || null,
    note: unknown.note || null,
    constraint: unknown.constraint || null,
    constraintFamily: unknown.constraint?.kind || null,
  };
};

/* -------------------------------------------------------------------------
 * THE WHOLE READ
 * ---------------------------------------------------------------------- */

export const FRONTS_WITHHELD_NOTE =
  'The per-front listing is withheld on a project this size. The band counts above are complete and stand; the fronts are not missing, they were not served.';

export const ORDERING_FALLBACK_NOTE =
  'Ordered by instance id alone. Nothing in this view ranks work by importance, urgency or value.';

export const RESEQUENCING_NOTE =
  'These fronts stand on the predecessor gate alone. Naming them changes no approved sequence and proposes nothing — the proposal is a separate, governed act.';

export const presentFlow = (payload) => {
  if (!payload) return null;
  const mappedFrom = payload.bandVocabulary?.mappedFrom || {};
  const bandsRaw = payload.bands || {};
  // The vocabulary's own order, which the contract states ranks nothing.
  // Object key order would be just as arbitrary and less traceable.
  const bandOrder = payload.bandVocabulary?.bands || Object.keys(bandsRaw);
  const withheld = payload.workFrontsWithheld === true;
  return {
    contractVersion: payload.contractVersion || null,
    projectId: payload.projectId || null,
    computedAt: payload.computedAt || null,
    sourceFingerprint: payload.sourceFingerprint || null,
    sourceFingerprintBasis: payload.sourceFingerprintBasis || null,
    bandBasis: payload.bandVocabulary?.basis || null,
    bandNote: payload.bandVocabulary?.note || null,
    clearanceWords: payload.clearanceVocabulary?.words || [],
    clearanceBasis: payload.clearanceVocabulary?.basis || null,
    clearanceNote: payload.clearanceVocabulary?.note || null,
    bands: bandOrder
      .filter((band) => bandsRaw[band])
      .map((band) => ({
        ...bandDisplay(band),
        count: bandsRaw[band].count ?? 0,
        instanceIds: bandsRaw[band].instanceIds || [],
        instanceIdsTruncated: bandsRaw[band].instanceIdsTruncated === true,
        mappedFrom: bandsRaw[band].mappedFrom || null,
      })),
    // NEVER re-sorted. The server's order is the whole of the ordering
    // contract, and re-sorting here would silently install a ranking.
    workFronts: (payload.workFronts || []).map((front) => presentWorkFront(front, mappedFrom)),
    workFrontCount: payload.workFrontCount ?? (payload.workFronts || []).length,
    workFrontsWithheld: withheld,
    workFrontsWithheldNote: withheld ? FRONTS_WITHHELD_NOTE : null,
    orderingKey: payload.ordering?.key || ['instanceId'],
    orderingNote: payload.ordering?.note || ORDERING_FALLBACK_NOTE,
    transitions: presentRecordedTransitions(payload.recordedTransitions),
    resourceCompatibility: presentResourceCompatibility(payload.resourceCompatibility),
    safeParallelWork: payload.safeParallelWork || [],
    safeParallelWorkTruncated: payload.safeParallelWorkTruncated === true,
    resequencingCandidates: payload.safeResequencingCandidates || [],
    resequencingCandidatesTruncated: payload.safeResequencingCandidatesTruncated === true,
    resequencingNote: RESEQUENCING_NOTE,
    conflicts: payload.conflicts || [],
    conflictCount: payload.conflictCount ?? (payload.conflicts || []).length,
    conflictsTruncated: payload.conflictsTruncated === true,
    unknowns: (payload.unknowns || []).map(presentFlowUnknown).filter(Boolean),
    missingInformation: payload.missingInformation || [],
    readOnly: payload.readOnly === true,
    aiDetermined: payload.aiDetermined === true,
    // A front list that is empty because it was withheld is a different
    // thing from a project with no work, and the panel must be able to
    // tell them apart without re-reading the flags.
    hasFronts: (payload.workFronts || []).length > 0,
  };
};

/** Fronts whose two names disagree — surfaced once for the whole read. */
export const bandDriftFronts = (flow) =>
  (flow?.workFronts || []).filter((front) => front && front.bandAgrees === false);
